import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "../../components/CustomButtons/Button.js";

import { useTranslation, Trans } from "react-i18next";

/**
 * Variante alternativa de la pantalla de política de privacidad.
 *
 * Se mantiene con el mismo patrón responsive que Privacy.js:
 * - contenedor fluido con maxWidth
 * - tipografía y espaciado adaptables
 * - CTA de descarga estable en móvil
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: "100%",
    maxWidth: 1100,
    margin: "0 auto",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
    },
  },

  cardTitle: {
    fontWeight: 500,
    fontSize: "1.5rem",

    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem",
    },
  },

  cardBody: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2.5),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },

  sectionTitle: {
    color: "#4f749f",
    fontWeight: 400,
    fontSize: "1.3rem",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),

    [theme.breakpoints.down("sm")]: {
      fontSize: "1.1rem",
      marginTop: theme.spacing(0.5),
    },
  },

  paragraph: {
    width: "100%",
    maxWidth: 780,
    margin: "0 auto",
    marginBottom: theme.spacing(1.5),
    fontSize: "1rem",
    lineHeight: 1.75,
    whiteSpace: "pre-line",
    wordBreak: "break-word",

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.95rem",
      lineHeight: 1.65,
    },
  },

  downloadWrapper: {
    width: "100%",
    maxWidth: 780,
    margin: "0 auto",
    marginTop: theme.spacing(1.5),
    textAlign: "center",
  },

  downloadButton: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: 320,
    },
  },
}));

export default function PrivacyAlt() {
  const classes = useStyles();
  const { t } = useTranslation("privacyPolicy");

  const sections = [
    { titleKey: "A1", descriptionKey: "descriptionA1", useTrans: false },
    { titleKey: "A2", descriptionKey: "descriptionA2", useTrans: false },
    { titleKey: "A3", descriptionKey: "descriptionA3", useTrans: false },
    { titleKey: "A4", descriptionKey: "descriptionA4", useTrans: false },
    { titleKey: "A5", descriptionKey: "descriptionA5", useTrans: false },
    { titleKey: "A6", descriptionKey: "descriptionA6", useTrans: false },
    { titleKey: "A7", descriptionKey: "descriptionA7", useTrans: false },
    { titleKey: "A8", descriptionKey: "descriptionA8", useTrans: true },
    { titleKey: "A9", descriptionKey: "descriptionA9", useTrans: false },
  ];

  return (
    <GridContainer justify="center" className={classes.wrapper}>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary">
            <Typography variant="h5" className={classes.cardTitle}>
              {t("title")}
            </Typography>
          </CardHeader>

          <CardBody className={classes.cardBody}>
            {sections.map((section) => (
              <React.Fragment key={section.titleKey}>
                <Typography
                  align="center"
                  variant="h5"
                  className={classes.sectionTitle}
                >
                  {t(section.titleKey)}
                </Typography>

                <Typography align="justify" className={classes.paragraph}>
                  {section.useTrans ? (
                    <Trans
                      i18nKey={section.descriptionKey}
                      ns="privacyPolicy"
                      components={{
                        dpjLink: (
                          <a
                            href="https://www.uned.es/dpj"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                        eofficeLink: (
                          <a
                            href="https://uned.sede.gob.es/"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                        privacyLink: (
                          <a
                            href="https://descargas.uned.es/publico/pdf/Politica_privacidad_UNED.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                        aepd: (
                          <a
                            href="https://www.aepd.es"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                  ) : (
                    t(section.descriptionKey)
                  )}
                </Typography>
              </React.Fragment>
            ))}

            <div className={classes.downloadWrapper}>
              <Button
                variant="contained"
                color="customBlue"
                component="a"
                href={`legal/politica-privacidad-${t("lang")}.pdf`}
                download
                className={classes.downloadButton}
              >
                {t("descargar")}
              </Button>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}